import { useRef } from 'react';
import type { DocumentReviewStatus } from '../../../../mocks/cases';
import CategoryHeader from './CategoryHeader';
import type { DocState } from './types';
import { randomSize, reviewStatusBadgeCls, reviewStatusLabel } from './utils';
import { DOCUMENT_FILE_ACCEPT } from '../../../../utils/documentUploadPolicy';

interface Props {
  docs: DocState[];
  onDocsChange: (next: DocState[]) => void;
  categoryReview: DocumentReviewStatus;
  onCategoryReviewChange: (status: DocumentReviewStatus) => void;
  onViewDocument?: (doc: DocState) => void;
}

const FAM_SUBCATS = [
  { key: 'marriage',     label: 'Marriage certificate' },
  { key: 'libro',        label: 'Libro de familia' },
  { key: 'birth',        label: 'Birth certificate' },
  { key: 'cohabitation', label: 'Cohabitation proof' },
  { key: 'school',       label: 'School enrollment' },
  { key: 'other',        label: 'Other' },
];

export default function FamilyCategory({ docs, onDocsChange, categoryReview, onCategoryReviewChange, onViewDocument }: Props) {
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const docsFor = (subKey: string) =>
    docs.filter((d) => d.category === 'family' && d.docType === subKey && d.uploaded);

  const handleFile = (subKey: string, file: File) => {
    onDocsChange([...docs, {
      id: `fam-${Date.now()}`, name: file.name, category: 'family', docType: subKey,
      uploaded: true, uploadedAt: new Date().toISOString().slice(0, 10),
      uploadedFile: file.name, rawFile: file, reviewStatus: 'pending' as DocumentReviewStatus, fileSize: randomSize(),
    }]);
  };

  const handleRemove = (id: string) => onDocsChange(docs.filter((d) => d.id !== id));

  return (
    <div className="max-w-[720px]">
      <CategoryHeader title="Family documentation" category="family" status={categoryReview} onChange={onCategoryReviewChange} />
      {FAM_SUBCATS.map((sub) => {
        const subDocs = docsFor(sub.key);
        return (
          <div key={sub.key} className="mb-5 max-w-[480px]">
            <p className="text-[14px] font-semibold text-slate-800 mt-5 mb-2">{sub.label}</p>

            {subDocs.map((doc) => (
              <div key={doc.id} className="bg-emerald-50 rounded-lg px-3 py-2 flex flex-col gap-2 mb-2 min-[420px]:flex-row min-[420px]:items-center">
                <i className="ri-file-check-line text-emerald-500 text-sm shrink-0" />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <p className="min-w-0 text-[12px] font-medium text-emerald-800 truncate">{doc.uploadedFile}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewStatusBadgeCls(doc.reviewStatus)}`}>
                    {reviewStatusLabel(doc.reviewStatus)}
                  </span>
                </div>
                <button type="button" onClick={() => onViewDocument?.(doc)} className="min-h-[36px] text-[11px] text-slate-600 font-medium border border-slate-200 rounded-md px-2 py-1 cursor-pointer shrink-0 whitespace-nowrap bg-white hover:bg-slate-50">
                  View
                </button>
                <button type="button" onClick={() => handleRemove(doc.id)} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-rose-500 cursor-pointer shrink-0">
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            ))}

            {subDocs.length === 0 ? (
              <div className="grid grid-cols-1 gap-2">
                <button type="button" onClick={() => fileRefs.current[sub.key]?.click()} className="min-h-[44px] flex items-center justify-center gap-1.5 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer">
                  <i className="ri-file-upload-line text-sm" /> Choose file
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRefs.current[sub.key]?.click()} className="w-full min-h-[44px] flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-200 rounded-xl font-medium text-[13px] text-slate-500 hover:border-slate-300 cursor-pointer">
                <i className="ri-add-line text-base" /> Add another
              </button>
            )}

            <input ref={(el) => { fileRefs.current[sub.key] = el; }} type="file" accept={DOCUMENT_FILE_ACCEPT} className="hidden"
              onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; handleFile(sub.key, file); e.target.value = ''; }} />
          </div>
        );
      })}
    </div>
  );
}
